// web/src/components/ChatPanel.tsx
// Photo-aware AI chat panel — collapsible side panel (desktop) / overlay (mobile)
// Supports session history, photo context injection, model selection

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChatService, type ChatMessage, type ChatSession, type PhotoContext } from '../services/chatService';
import { EngramService } from '../services/engramService';
import { PrismStore } from '../services/prismStore';
import type { PhotoScore, PrismEntitlements } from '@frame/scoring-types';

// ─── Design tokens (match Home.tsx) ───────────────────────
const C = {
  bg: '#0f172a', text: '#f8fafc', teal: '#50B8A8', tealLight: 'rgba(80,184,168,0.12)',
  tealMedium: 'rgba(80,184,168,0.22)', surface: '#1e293b',
  secondary: '#94a3b8', divider: '#334155', error: 'rgba(239, 68, 68, 0.7)',
  white: '#FFFFFF', blue: '#5186B0',
  surfaceHover: '#334155', muted: '#64748b',
};

function scoreColor(score: number): string {
  if (score < 3) return '#EF4444';
  if (score < 5) return '#F59E0B';
  if (score < 7) return '#50B8A8';
  return '#059669';
}

// ─── Props ────────────────────────────────────────────────
export interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  activeSessionId: string | null;
  onSessionChange: (sessionId: string) => void;
  onSessionsUpdate: (sessions: ChatSession[]) => void;
  scoredPhotos: PhotoScore[];   // Currently scored photos available for context
  scoringMode: string;
  prismEntitlements?: PrismEntitlements;
  onUpgrade?: () => void;
}

// ─── Mini Photo Card (inline in chat) ─────────────────────
function MiniPhotoCard({ photo }: { photo: PhotoContext }) {
  return (
    <div style={{
      display: 'flex', gap: 8, padding: 8, background: C.surface,
      borderRadius: 8, border: `1px solid ${C.divider}`, marginTop: 4, marginBottom: 4,
    }}>
      {photo.thumbnailDataUrl && (
        <img src={photo.thumbnailDataUrl} alt={photo.fileName}
          style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: C.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {photo.fileName}
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: scoreColor(photo.compositeScore) }}>
          {photo.compositeScore.toFixed(1)}/10
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          {Object.entries(photo.dimensions).map(([dim, score]) => (
            <span key={dim} style={{ fontSize: 10, color: C.muted }}>
              {dim.replace(/([A-Z])/g, ' $1').trim()}: <span style={{ color: scoreColor(score as number) }}>{(score as number).toFixed(1)}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Message Bubble ───────────────────────────────────────
function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === 'user';
  return (
    <div style={{
      display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: 12, animation: 'frameFadeIn 0.2s ease-out',
    }}>
      <div style={{
        maxWidth: '85%', padding: '10px 14px', borderRadius: 14,
        background: isUser
          ? 'linear-gradient(135deg, #50B8A8 0%, #3B8CB8 50%, #5186B0 100%)'
          : C.surface,
        color: isUser ? C.white : C.text,
        border: isUser ? 'none' : `1px solid ${C.divider}`,
        fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word',
        borderBottomRightRadius: isUser ? 4 : 14,
        borderBottomLeftRadius: isUser ? 14 : 4,
      }}>
        {/* Render photo context cards if present */}
        {msg.photoContext?.map((p, i) => <MiniPhotoCard key={i} photo={p} />)}
        {/* Simple markdown-ish rendering: bold, newlines */}
        {msg.content.split('\n').map((line, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {line.split(/(\*\*.*?\*\*)/).map((part, j) =>
              part.startsWith('**') && part.endsWith('**')
                ? <strong key={j}>{part.slice(2, -2)}</strong>
                : <span key={j}>{part}</span>
            )}
          </React.Fragment>
        ))}
        <div style={{ fontSize: 10, color: isUser ? 'rgba(255,255,255,0.5)' : C.muted, marginTop: 4 }}>
          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}

// ─── Photo Selector (attach scored photos to message) ─────
function PhotoSelector({ photos, onSelect, onClose }: {
  photos: PhotoScore[];
  onSelect: (selected: PhotoScore[]) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div style={{
      position: 'absolute', bottom: 60, left: 8, right: 8,
      background: C.bg, border: `1px solid ${C.divider}`, borderRadius: 12,
      padding: 12, maxHeight: 240, overflowY: 'auto', zIndex: 10,
      boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Attach photos for context</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18 }}>&times;</button>
      </div>
      {photos.length === 0 && (
        <div style={{ fontSize: 13, color: C.muted, padding: 16, textAlign: 'center' }}>
          Score some photos first to attach them to the conversation.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 6 }}>
        {photos.map(p => (
          <div key={p.id} onClick={() => toggle(p.id)} style={{
            position: 'relative', cursor: 'pointer', borderRadius: 8, overflow: 'hidden',
            border: selected.has(p.id) ? `2px solid ${C.teal}` : `2px solid transparent`,
            opacity: selected.has(p.id) ? 1 : 0.7,
          }}>
            {p.thumbnailUri && <img src={p.thumbnailUri} alt="" style={{ width: '100%', height: 64, objectFit: 'cover' }} />}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2px 4px',
              background: 'rgba(0,0,0,0.7)', fontSize: 11, color: scoreColor(p.compositeScore),
              fontWeight: 600, textAlign: 'center',
            }}>
              {p.compositeScore.toFixed(1)}
            </div>
            {selected.has(p.id) && (
              <div style={{
                position: 'absolute', top: 2, right: 2, width: 18, height: 18,
                borderRadius: '50%', background: C.teal, display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: 12, color: C.bg,
              }}>&#10003;</div>
            )}
          </div>
        ))}
      </div>
      {selected.size > 0 && (
        <button onClick={() => {
          onSelect(photos.filter(p => selected.has(p.id)));
          onClose();
        }} style={{
          marginTop: 8, width: '100%', padding: '8px 0', borderRadius: 8,
          background: `linear-gradient(135deg, #50B8A8 0%, #3B8CB8 50%, #5186B0 100%)`,
          color: C.white, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>
          Attach {selected.size} photo{selected.size > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}

// ─── Session Drawer ───────────────────────────────────────
function SessionDrawer({ sessions, activeSessionId, onSelect, onNew, onDelete, onClose }: {
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  return (
    <div style={{
      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
      background: C.bg, zIndex: 20, display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', borderBottom: `1px solid ${C.divider}`,
      }}>
        <span style={{ fontSize: 15, fontWeight: 700, color: C.teal }}>Chat History</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onNew} style={{
            background: C.surface, border: `1px solid ${C.divider}`, color: C.teal,
            padding: '4px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600,
          }}>+ New</button>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 20,
          }}>&times;</button>
        </div>
      </div>
      {/* Sessions list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
        {sessions.length === 0 && (
          <div style={{ textAlign: 'center', padding: 32, color: C.muted, fontSize: 13 }}>
            No conversations yet. Start chatting!
          </div>
        )}
        {sessions.map(s => (
          <div key={s.sessionId} onClick={() => { onSelect(s.sessionId); onClose(); }}
            style={{
              padding: '10px 12px', borderRadius: 8, marginBottom: 4, cursor: 'pointer',
              background: s.sessionId === activeSessionId ? C.tealLight : 'transparent',
              border: s.sessionId === activeSessionId ? `1px solid ${C.teal}` : `1px solid transparent`,
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>
                {s.title}
              </div>
              <button onClick={(e) => { e.stopPropagation(); onDelete(s.sessionId); }}
                style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
                title="Delete conversation">&#128465;</button>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {new Date(s.lastMessageAt).toLocaleDateString()} &middot; {s.messageCount} msg{s.messageCount !== 1 ? 's' : ''}
              {s.photoCount > 0 && ` · ${s.photoCount} photo${s.photoCount !== 1 ? 's' : ''}`}
            </div>
            {s.preview && (
              <div style={{ fontSize: 12, color: C.secondary, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.preview}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Model Selector ───────────────────────────────────────
const MODEL_OPTIONS = [
  { value: 'bedrock-claude', label: 'Claude — Photo Expert' },
  { value: 'openai', label: 'GPT — General' },
  { value: 'gemini', label: 'Gemini — Visual' },
  { value: 'ollama', label: 'Local Model' },
];

// ═══════════════════════════════════════════════════════════
// MAIN CHAT PANEL
// ═══════════════════════════════════════════════════════════

export function ChatPanel({
  isOpen, onClose, userId, activeSessionId, onSessionChange,
  onSessionsUpdate, scoredPhotos, scoringMode, prismEntitlements, onUpgrade,
}: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [attachedPhotos, setAttachedPhotos] = useState<PhotoScore[]>([]);
  const [showSessionDrawer, setShowSessionDrawer] = useState(false);
  const [selectedModel, setSelectedModel] = useState('bedrock-claude');
  const [prismChatMode, setPrismChatMode] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load messages when session changes
  useEffect(() => {
    if (activeSessionId) {
      setMessages(ChatService.getMessages(activeSessionId));
    } else {
      setMessages([]);
    }
  }, [activeSessionId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  const MAX_MESSAGE_LENGTH = 5000;

  const handleSend = useCallback(async () => {
    if (!input.trim() || sending || !activeSessionId) return;
    const msg = input.trim().slice(0, MAX_MESSAGE_LENGTH);
    setInput('');
    setSending(true);

    // Build photo context: use explicitly attached photos, or auto-include all scored photos
    const contextSource = attachedPhotos.length > 0
      ? attachedPhotos
      : [...scoredPhotos]
          .sort((a, b) => b.compositeScore - a.compositeScore);
    const photoCtx: PhotoContext[] = contextSource.map(p => ({
      fileName: p.assetIdentifier || p.id,
      thumbnailDataUrl: p.thumbnailUri || '',
      compositeScore: p.compositeScore,
      dimensions: p.dimensions as unknown as Record<string, number>,
      dimensionNotes: p.dimensionNotes,
      detractorPenalty: p.detractorPenalty,
      detectedFaceCount: p.detectedFaceCount,
      landmarkTier: String(p.landmarkTier),
      editPlan: p.editPlan ? { summary: p.editPlan.summary, weakestDimension: p.editPlan.weakestDimension, topEdit: p.editPlan.topEdit } : null,
    }));

    // Optimistically add user message to UI
    const userMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: msg,
      photoContext: photoCtx.length > 0 ? photoCtx : undefined,
      timestamp: Date.now(),
    };
    setMessages(prev => [...prev, userMsg]);
    setAttachedPhotos([]);

    try {
      // Build PRISM context for the chat if in PRISM mode
      let prismContext: string | undefined;
      if (prismChatMode) {
        const pv = PrismStore.getPreferenceVector();
        if (pv.photosSampled > 0) {
          const traits: string[] = [];
          if (Math.abs(pv.warmthPreference) > 0.15) traits.push(pv.warmthPreference > 0 ? 'warm tones' : 'cool tones');
          if (Math.abs(pv.contrastPreference) > 0.15) traits.push(pv.contrastPreference > 0 ? 'high contrast' : 'soft contrast');
          if (Math.abs(pv.saturationPreference) > 0.15) traits.push(pv.saturationPreference > 0 ? 'vibrant colors' : 'muted colors');
          if (Math.abs(pv.cropPreference) > 0.15) traits.push(pv.cropPreference > 0 ? 'tight crops' : 'wide shots');
          if (Math.abs(pv.smilePreference) > 0.15) traits.push(pv.smilePreference > 0 ? 'smiling expressions' : 'serious expressions');
          if (Math.abs(pv.backgroundPreference) > 0.15) traits.push(pv.backgroundPreference > 0 ? 'clean backgrounds' : 'contextual backgrounds');
          prismContext = `User's PRISM Style DNA (${pv.photosSampled} photos sampled, confidence: ${(pv.confidence * 100).toFixed(0)}%): ${traits.length > 0 ? traits.join(', ') : 'still learning'}.`;
          prismContext += ` Dimension affinities — Portrait: ${pv.portraitAffinity.toFixed(2)}, Lighting: ${pv.lightingAffinity.toFixed(2)}, Composition: ${pv.compositionAffinity.toFixed(2)}, Scene: ${pv.sceneAffinity.toFixed(2)}.`;
        }
      }

      const assistantMsg = await ChatService.sendMessage({
        sessionId: activeSessionId,
        message: msg,
        history: [],
        photoContext: photoCtx.length > 0 ? photoCtx : undefined,
        modelProvider: selectedModel,
        scoringMode,
        prismEnabled: prismChatMode,
        userId,
        prismContext,
      });
      // Reload messages from storage (authoritative source)
      setMessages(ChatService.getMessages(activeSessionId));
      // Update sessions list
      onSessionsUpdate(ChatService.getSessions(userId));
    } catch (err) {
      console.error('[FRAME Chat] Send error:', err);
    } finally {
      setSending(false);
    }
  }, [input, sending, activeSessionId, attachedPhotos, selectedModel, scoringMode, userId, onSessionsUpdate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewSession = () => {
    const session = ChatService.createSession(userId);
    onSessionChange(session.sessionId);
    onSessionsUpdate(ChatService.getSessions(userId));
    setShowSessionDrawer(false);
  };

  const handleDeleteSession = (sessionId: string) => {
    ChatService.deleteSession(sessionId, userId);
    const sessions = ChatService.getSessions(userId);
    onSessionsUpdate(sessions);
    if (sessionId === activeSessionId) {
      if (sessions.length > 0) {
        onSessionChange(sessions[0].sessionId);
      } else {
        // Create a fresh session
        const s = ChatService.createSession(userId);
        onSessionChange(s.sessionId);
        onSessionsUpdate(ChatService.getSessions(userId));
      }
    }
  };

  if (!isOpen) return null;

  const sessions = ChatService.getSessions(userId);

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: 'min(420px, 100vw)', background: C.bg,
      borderLeft: `1px solid ${C.divider}`, display: 'flex', flexDirection: 'column',
      zIndex: 1000, boxShadow: '-4px 0 24px rgba(0,0,0,0.5)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
        borderBottom: `1px solid ${C.divider}`, flexShrink: 0,
      }}>
        {/* History button */}
        <button onClick={() => setShowSessionDrawer(true)} style={{
          background: 'none', border: 'none', color: C.secondary, cursor: 'pointer',
          fontSize: 18, padding: '2px 6px', display: 'flex', alignItems: 'center',
        }} title="Chat history">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {prismChatMode ? 'PRISM Advisor' : 'Photo Advisor'}
          </div>
          {/* PRISM toggle — prominent switch */}
          <button
            onClick={() => {
              if (!prismEntitlements?.chatMode) {
                onUpgrade?.();
                return;
              }
              setPrismChatMode(!prismChatMode);
            }}
            title={prismEntitlements?.chatMode ? (prismChatMode ? 'Switch to standard Photo Advisor' : 'Activate PRISM — personalized advice based on your style') : 'Upgrade to Gold to unlock PRISM chat mode'}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginTop: 4, padding: '4px 12px', borderRadius: 14,
              fontSize: 11, fontWeight: 700, border: '1px solid',
              cursor: 'pointer', transition: 'all 0.2s',
              background: prismChatMode
                ? 'linear-gradient(135deg, #50B8A8, #5186B0)'
                : prismEntitlements?.chatMode ? C.surface : 'transparent',
              borderColor: prismChatMode
                ? 'transparent'
                : prismEntitlements?.chatMode ? C.teal : C.divider,
              color: prismChatMode ? C.white : prismEntitlements?.chatMode ? C.teal : C.muted,
              opacity: prismEntitlements?.chatMode ? 1 : 0.5,
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: prismChatMode ? C.white : prismEntitlements?.chatMode ? C.teal : C.muted,
              boxShadow: prismChatMode ? '0 0 6px rgba(255,255,255,0.5)' : 'none',
              transition: 'all 0.2s',
            }} />
            {prismChatMode ? 'PRISM ON' : 'PRISM OFF'}
            {!prismEntitlements?.chatMode && <span style={{ fontSize: 9, marginLeft: 2 }}>&#128274;</span>}
          </button>
          {/* Model selector */}
          <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)}
            style={{
              background: 'transparent', border: 'none', color: C.muted, fontSize: 11,
              cursor: 'pointer', padding: 0, outline: 'none',
            }}>
            {MODEL_OPTIONS.map(o => (
              <option key={o.value} value={o.value} style={{ background: C.surface }}>{o.label}</option>
            ))}
          </select>
        </div>

        {/* New chat button */}
        <button onClick={handleNewSession} style={{
          background: C.surface, border: `1px solid ${C.divider}`, color: C.teal,
          padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600,
        }} title="New conversation">+</button>

        {/* Close button */}
        <button onClick={onClose} style={{
          background: 'none', border: 'none', color: C.muted, cursor: 'pointer',
          fontSize: 22, lineHeight: 1, padding: '0 4px',
        }}>&times;</button>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto', padding: '12px 16px',
        display: 'flex', flexDirection: 'column',
      }} className="frame-scroll">
        {messages.length === 0 && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center',
          }}>
            <div style={{ fontSize: 36 }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={C.teal} strokeWidth="1.5">
                <rect x="2" y="6" width="20" height="14" rx="3" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <circle cx="12" cy="13" r="3" />
              </svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>
              {prismChatMode ? 'PRISM Personal Advisor' : 'FRAME Photo Advisor'}
            </div>
            <div style={{ fontSize: 13, color: C.secondary, maxWidth: 280, marginBottom: 16 }}>
              {prismChatMode
                ? 'Your personal photo curation advisor. Understands your visual style and helps you curate your best self.'
                : 'Ask about your scored photos, get editing advice, or discuss photography techniques.'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 280 }}>
              {(prismChatMode
                ? ['What does my visual style say about me?', 'Which photos best represent my personal brand?', 'Help me curate photos for my dating profile']
                : ['Why did my photos score low?', 'How can I improve my lighting?', 'Which photo should I use for my profile?']
              ).map(prompt => (
                <button key={prompt} onClick={() => { setInput(prompt); }} style={{
                  background: C.surface, border: `1px solid ${C.divider}`, borderRadius: 10,
                  padding: '10px 14px', color: C.text, fontSize: 13, cursor: 'pointer',
                  textAlign: 'left', transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = C.teal)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = C.divider)}
                >{prompt}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map(msg => <MessageBubble key={msg.id} msg={msg} />)}
        {sending && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
            <div style={{
              padding: '10px 14px', borderRadius: 14, background: C.surface,
              border: `1px solid ${C.divider}`, borderBottomLeftRadius: 4,
              color: C.muted, fontSize: 14,
            }}>
              Thinking...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Attached photos preview */}
      {attachedPhotos.length > 0 && (
        <div style={{
          padding: '6px 16px', borderTop: `1px solid ${C.divider}`,
          display: 'flex', gap: 6, overflowX: 'auto', flexShrink: 0,
        }}>
          {attachedPhotos.map(p => (
            <div key={p.id} style={{ position: 'relative', flexShrink: 0 }}>
              {p.thumbnailUri && <img src={p.thumbnailUri} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} />}
              <button onClick={() => setAttachedPhotos(prev => prev.filter(x => x.id !== p.id))}
                style={{
                  position: 'absolute', top: -4, right: -4, width: 16, height: 16,
                  borderRadius: '50%', background: C.error, color: C.white, border: 'none',
                  fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>&times;</button>
            </div>
          ))}
          <span style={{ fontSize: 11, color: C.muted, alignSelf: 'center' }}>
            {attachedPhotos.length} photo{attachedPhotos.length > 1 ? 's' : ''} attached
          </span>
        </div>
      )}

      {/* Input area */}
      <div style={{
        padding: '8px 12px', borderTop: `1px solid ${C.divider}`,
        display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0,
        position: 'relative',
      }}>
        {showPhotoSelector && (
          <PhotoSelector
            photos={scoredPhotos}
            onSelect={selected => setAttachedPhotos(prev => [...prev, ...selected.filter(s => !prev.some(p => p.id === s.id))])}
            onClose={() => setShowPhotoSelector(false)}
          />
        )}

        {/* Attach photos button */}
        <button onClick={() => setShowPhotoSelector(!showPhotoSelector)}
          style={{
            background: 'none', border: 'none', color: scoredPhotos.length > 0 ? C.teal : C.muted,
            cursor: scoredPhotos.length > 0 ? 'pointer' : 'default', fontSize: 20,
            padding: '4px', flexShrink: 0, opacity: scoredPhotos.length > 0 ? 1 : 0.4,
          }}
          disabled={scoredPhotos.length === 0}
          title={scoredPhotos.length > 0 ? 'Attach scored photos' : 'Score photos first to attach them'}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </button>

        {/* Text input */}
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          onKeyDown={handleKeyDown}
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder="Ask about your photos..."
          rows={1}
          style={{
            flex: 1, background: C.surface, border: `1px solid ${C.divider}`,
            borderRadius: 12, padding: '8px 12px', color: C.text, fontSize: 14,
            resize: 'none', outline: 'none', fontFamily: 'inherit',
            maxHeight: 100, overflowY: 'auto', lineHeight: 1.4,
          }}
          onInput={e => {
            const el = e.target as HTMLTextAreaElement;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 100) + 'px';
          }}
        />

        {/* Send button */}
        <button onClick={handleSend} disabled={!input.trim() || sending}
          style={{
            background: input.trim() && !sending
              ? `linear-gradient(135deg, #50B8A8 0%, #3B8CB8 50%, #5186B0 100%)`
              : C.surface,
            border: 'none', borderRadius: '50%', width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: input.trim() && !sending ? 'pointer' : 'default',
            flexShrink: 0, opacity: input.trim() && !sending ? 1 : 0.4,
          }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>

      {/* Session Drawer overlay */}
      {showSessionDrawer && (
        <SessionDrawer
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSelect={onSessionChange}
          onNew={handleNewSession}
          onDelete={handleDeleteSession}
          onClose={() => setShowSessionDrawer(false)}
        />
      )}
    </div>
  );
}
